import type { RequirementCompleteness } from '@ispot/shared'

interface Props {
  completeness: RequirementCompleteness | null | undefined
}

/**
 * Compact completeness signal for the table view.
 *   ▮▮▮▯▯  3/5
 * Empty (no template) renders an em-dash so column width stays stable.
 */
export function CompletenessCell({ completeness }: Props) {
  if (!completeness) {
    return <span style={{ color: 'var(--color-warm-400)', fontSize: 12 }}>—</span>
  }
  const { completed, total } = completeness
  if (total === 0) {
    return <span style={{ color: 'var(--color-fg-muted)', fontSize: 12 }}>n/a</span>
  }

  const ratio = completed / total
  const tone =
    ratio >= 1 ? 'var(--color-success)'
    : completed === 0 ? 'var(--color-warm-400)'
    : 'var(--color-warning)'

  return (
    <span
      className="inline-flex items-center gap-2 tabular-nums"
      style={{ fontSize: 12, color: 'var(--color-fg-secondary)' }}
      title={`${completeness.templateName} · ${completed} of ${total} required sections complete`}
    >
      <span
        aria-hidden
        style={{
          width: 40,
          height: 4,
          borderRadius: 2,
          background: 'var(--color-warm-200)',
          overflow: 'hidden',
          flexShrink: 0,
        }}
      >
        <span
          style={{
            display: 'block',
            height: '100%',
            width: `${Math.round(ratio * 100)}%`,
            background: tone,
            transition: 'width 150ms cubic-bezier(0.2,0,0,1)',
          }}
        />
      </span>
      <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11 }}>
        {completed}/{total}
      </span>
    </span>
  )
}
